import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { ButtonsBarService, ViewState } from '@ms/core';
import { KeyValueDTO } from '@ms/core/model/keyvalue.model';
import { logRequest } from 'app/main/shared/http-utils';
import { map, Observable, Subject } from 'rxjs';
import { SchoolDepartment } from '../school-department/model/school-department.model';
import { SchoolService } from './school.service';
import { DepartmentShareService } from './department-share.service';

@Injectable({
    providedIn: 'root',
})
export class SchoolDepartmentService implements OnDestroy {
    private _subjectNew = new Subject<Event>();
    baseUrl = '/school-service/api/department';
    permittedDepartments: string[] = [];
    shareService = inject(DepartmentShareService);
    constructor(
        private buttonBarService: ButtonsBarService,
        private http: HttpClient,
        private schoolService: SchoolService
    ) {}

    getAll(schoolId: string, filter: boolean): Observable<SchoolDepartment[]> {
        return this.http
            .get<SchoolDepartment[]>(`/school-service/api/school/${schoolId}/department`)
            .pipe(
                map(response =>
                    filter && !this.shareService.isAdmin
                        ? response.filter(d => this.shareService.departments.includes(d.id))
                        : response
                )
            );
    }

    getAllActive(schoolId: string): Observable<SchoolDepartment[]> {
        return this.http.get<SchoolDepartment[]>(`/school-service/api/school/${schoolId}/department/active`);
    }

    getById(id: string): Observable<HttpResponse<SchoolDepartment>> {
        return this.http.get<SchoolDepartment>(`${this.baseUrl}/${id}`, { observe: 'response' });
    }

    create(department: SchoolDepartment): Observable<HttpResponse<SchoolDepartment>> {
        return this.http.post<SchoolDepartment>(this.baseUrl, department, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    update(department: SchoolDepartment): Observable<HttpResponse<SchoolDepartment>> {
        return this.http.put<SchoolDepartment>(this.baseUrl, department, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    delete(id: number): Observable<HttpResponse<any>> {
        return this.http.delete(`${this.baseUrl}/${id}`, { observe: 'response', headers: logRequest() });
    }

    // New
    doNew(event: Event) {
        this._subjectNew.next(event);
        this.buttonBarService.changeViewState(ViewState.NEW);
    }

    get new() {
        return this._subjectNew;
    }

    ngOnDestroy() {
        console.log('School School Department service destroyed!');
        // Clean subscriptions, intervals, etc
        this._subjectNew.unsubscribe();
    }

    map(department: SchoolDepartment): SchoolDepartment {
        const result: SchoolDepartment = Object.assign({}, department);
        console.log('Map Result:', result);
        return result;
    }

    getDepartmentsKeyValueDTO(schoolId: string, filter: boolean): Observable<KeyValueDTO[]> {
        return this.http
            .get<KeyValueDTO[]>(`${this.baseUrl}/active/select/${schoolId}`)
            .pipe(
                map(response =>
                    filter && !this.shareService.isAdmin
                        ? response.filter(d => this.shareService.departments.includes(d.id))
                        : response
                )
            );
    }

    getSectionsByDepartmentName(departmentName: string): Observable<KeyValueDTO[]> {
        const schoolId = this.schoolService.getActiveSchool().id;
        return this.http.get<KeyValueDTO[]>(`${this.baseUrl}/school/${schoolId}/department-name/${departmentName}`);
    }

    getSectionsKeyValueDTO(): Observable<KeyValueDTO[]> {
        const schoolId = this.schoolService.getActiveSchool().id;
        return this.http.get<KeyValueDTO[]>(`${this.baseUrl}/school/${schoolId}/sections`);
    }
}
